import { useMutation, useQuery, useQueryClient } from 'react-query';

import { approveClaimRequest, approveClaimRequestWithToken, AskClaimRestaurantParams, askRestaurantClaimRequest, checkClaimTokenRequest, ClaimApproveRejectResponse, ClaimApproveRejectWithTokenResponse, ClaimCheckTokenResponse, ClaimResponse, ClaimRestaurantResponse, FetchClaimOptions, fetchClaimRequest, fetchClaimsByOwnerRequest, rejectClaimRequest, rejectClaimRequestWithToken } from '../lib/claim';
import { ErrorResponse, UseQueryConfig } from '../lib/types';


export const useGetClaimsByOwner = () => {
  return useQuery(['claims'], () => fetchClaimsByOwnerRequest());
};

export const useFetchClaimQuery = ({ id }: FetchClaimOptions, config?: UseQueryConfig<ClaimResponse>) => {
  return useQuery<ClaimResponse, ErrorResponse>({
    queryKey: ['claim', id],
    queryFn: () => fetchClaimRequest(id),
    ...config
  });
};

export const useCheckClaimTokenQuery = (token: string, config?: UseQueryConfig<ClaimCheckTokenResponse>) => {
  return useQuery<ClaimCheckTokenResponse, ErrorResponse>({
    queryKey: ['token'],
    queryFn: () => checkClaimTokenRequest(token),
    ...config
  });
};

export const useAskClaimRestaurantMutation = ({ onSuccess }: { onSuccess?: (response: ClaimRestaurantResponse, params: AskClaimRestaurantParams) => void }) => {
  const queryClient = useQueryClient();
  
  return useMutation<ClaimRestaurantResponse, ErrorResponse, { restaurantId: string; params: AskClaimRestaurantParams }>(
    ({ restaurantId, params }) => askRestaurantClaimRequest(restaurantId, params),  
    {
      onSuccess: (data, { params }) => { 
        if (onSuccess) {
          queryClient.invalidateQueries({ queryKey: ['claims'] });
          onSuccess(data, params);
        }
      }
    }
  );
};

export const useApproveClaimWithTokenMutation = ({ onSuccess }: { onSuccess?: (response: ClaimApproveRejectWithTokenResponse) => void }) => {
  const queryClient = useQueryClient();
  
  return useMutation<ClaimApproveRejectWithTokenResponse, ErrorResponse, string>(
    (token) => approveClaimRequestWithToken(token),  
    {
      onSuccess: (data) => { 
        if (onSuccess) {
          queryClient.invalidateQueries({ queryKey: ['claims'] });
          onSuccess(data);
        }
      }
    }
  );
}

export const useApproveClaimMutation = ({ onSuccess }: { onSuccess?: (response: ClaimApproveRejectResponse) => void }) => {
  const queryClient = useQueryClient();
  
  return useMutation<ClaimApproveRejectResponse, ErrorResponse, string>(
    (claimId) => approveClaimRequest(claimId),  
    {
      onSuccess: (data) => { 
        if (onSuccess) {
          queryClient.invalidateQueries({ queryKey: ['claims'] });
          onSuccess(data);
        }
      }
    }
  );
}

export const useRejectClaimWithTokenMutation = ({ onSuccess }: { onSuccess?: (response: ClaimApproveRejectWithTokenResponse) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<ClaimApproveRejectWithTokenResponse, ErrorResponse, string>(
    (token) => rejectClaimRequestWithToken(token),  
    {
      onSuccess: (data) => { 
        if (onSuccess) {
          queryClient.invalidateQueries({ queryKey: ['claims'] });
          onSuccess(data);
        }
      }
    }
  );
}

export const useRejectClaimMutation = ({ onSuccess }: { onSuccess?: (response: ClaimApproveRejectResponse) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<ClaimApproveRejectResponse, ErrorResponse, string>(
    (claimId) => rejectClaimRequest(claimId),  
    {
      onSuccess: (data) => { 
        if (onSuccess) {
          queryClient.invalidateQueries({ queryKey: ['claims'] });
          onSuccess(data);
        }
      }
    }
  );
}
